import {
  CHECKLIST_MAX_ITEMS,
  MARGIN_MM,
  SPACING_MM,
  TITLE_MAX_CHARS,
} from '../print-forms/model';
import type { PrintContentPack } from './types';

const S = SPACING_MM;
const M = MARGIN_MM;

export const printContentEn: PrintContentPack = {
  formNames: {
    lined: 'Lined paper',
    grid: 'Graph paper',
    weekly: 'Weekly planner',
    checklist: 'Checklist',
  },
  formBlurbs: {
    lined: `Ruled paper with ${S.lined.min}–${S.lined.max} mm line spacing and an optional title.`,
    grid: `Square grid with ${S.grid.min}–${S.grid.max} mm cells and a heavier line every 5 cells.`,
    weekly: 'One week on a page, starting Monday or Sunday, plus a notes box.',
    checklist: 'Tick boxes with writing lines; items can be printed in advance.',
  },

  homeHeading: 'Create printable forms',
  homeIntro:
    'Make lined paper, graph paper, a weekly planner or a checklist as an A4 or US Letter PDF and print it. Set the title, margins and spacing; the file you download is the one shown in the preview.',

  hub: {
    title: 'Create printable forms — lined paper, graph paper, weekly planner, checklist',
    description:
      'Make lined paper, graph paper, a weekly planner or a checklist as an A4 or US Letter PDF. Choose the title, margins and spacing, preview it and print at actual size.',
    h1: 'Create printable forms',
    intro: [
      'Pick a form, then set the title, paper size, margins and spacing to get a one-page PDF. The preview is a rendering of the exact PDF you will download.',
      'The title and items you type are used inside this browser only and are not sent anywhere. There is no account.',
    ],
    faq: [
      {
        q: 'Which paper sizes are available?',
        a: 'A4 (210 × 297 mm) and US Letter (8.5 × 11 in). Pick the size of the paper in your printer.',
      },
      {
        q: 'Can I make several pages at once?',
        a: 'Each PDF is one page. For more sheets, raise the number of copies in the print dialog.',
      },
    ],
  },

  forms: {
    lined: {
      title: 'Lined paper PDF maker — set the line spacing, print on A4 or Letter',
      description: `Make lined paper as a PDF with ${S.lined.min}–${S.lined.max} mm line spacing, your own margins and an optional title. A4 and US Letter, downloaded straight from the browser.`,
      h1: 'Make lined paper',
      intro: [
        `One sheet of horizontal rules. ${S.lined.default} mm is close to college-ruled notebook paper; handwriting practice for young children usually wants 10–12 mm.`,
      ],
      steps: [
        'Choose the paper size (A4 or Letter).',
        `Type a title for the top of the page if you want one (up to ${TITLE_MAX_CHARS} characters; leave it empty for none).`,
        `Set the margins (${M.min}–${M.max} mm) and line spacing (${S.lined.min}–${S.lined.max} mm) and check the preview.`,
        'Press “Download PDF”, then print at actual size (100%).',
      ],
      faq: [
        {
          q: 'What line spacing should I use?',
          a: 'Adults usually write comfortably on 7–8 mm. Early handwriting practice tends to use 10–12 mm. The preview redraws on every change, so compare them directly.',
        },
        {
          q: 'The printed spacing is slightly off.',
          a: 'If “Fit to page” is on in the print dialog, the whole page is shrunk. Switch to “Actual size” or 100% scale.',
        },
      ],
    },

    grid: {
      title: 'Graph paper PDF maker — choose the cell size in millimetres',
      description: `Make graph paper as a PDF with ${S.grid.min}–${S.grid.max} mm square cells and a heavier line every five cells. Choose A4 or US Letter and download it from the browser.`,
      h1: 'Make graph paper',
      intro: [
        `A sheet of square cells. ${S.grid.default} mm is the common size; every fifth line is darker so distances are easy to count.`,
      ],
      steps: [
        'Choose the paper size and, optionally, a title.',
        `Set the cell size (${S.grid.min}–${S.grid.max} mm) and margins. Only whole cells that fit inside the margins are drawn, centred so the left and right margins match.`,
        'Check the preview and press “Download PDF”.',
        'Print at actual size (100%) for the cells to measure correctly.',
      ],
      faq: [
        {
          q: 'Are the cells exactly the size I set?',
          a: 'In the PDF, yes. On paper it depends on the print scale and the printer, so print at actual size and check one sheet with a ruler.',
        },
        {
          q: 'Why is there extra space at the sides?',
          a: 'Partial cells are never drawn. The width left over after the last whole cell is split between the two side margins.',
        },
      ],
    },

    weekly: {
      title: 'Weekly planner PDF maker — week starting Monday or Sunday',
      description:
        'Make a one-page weekly planner PDF that starts on Monday or Sunday. Each day has writing lines and a blank for the date, plus a notes box. A4 and US Letter.',
      h1: 'Make a weekly planner',
      intro: [
        'The seven days and a notes box in two columns on one sheet. Dates are left blank next to each day name, so the same PDF works for any week.',
      ],
      steps: [
        'Choose the paper size and whether the week starts on Monday or Sunday.',
        'Add a title such as “Week of June 9” if you like.',
        'Set the margins and check the layout in the preview.',
        'Press “Download PDF” and print at actual size (100%).',
      ],
      faq: [
        {
          q: 'Can the day names be in Korean?',
          a: 'Yes — make it on the Korean page (한국어) and the days print as 월요일, 화요일 and so on.',
        },
        {
          q: 'Are dates printed?',
          a: 'No. Write them in the blank next to each day, and reprint the same file every week.',
        },
      ],
    },

    checklist: {
      title: 'Checklist PDF maker — tick boxes with your own items',
      description: `Make a printable checklist PDF with tick boxes and writing lines. Pre-print up to ${CHECKLIST_MAX_ITEMS} items, one per line, and set the row height and margins.`,
      h1: 'Make a checklist',
      intro: [
        'A list with a tick box on the left and a writing line on the right. For lists you reuse — groceries, packing, chores — type the items once and print it ready to use.',
      ],
      steps: [
        'Choose the paper size and type a title.',
        `Type items one per line, or leave the box empty for a blank checklist (up to ${CHECKLIST_MAX_ITEMS}).`,
        `Set the row height (${S.checklist.min}–${S.checklist.max} mm) and margins. You are told if there are more items than rows or an item does not fit on its line.`,
        'Press “Download PDF” and print at actual size (100%).',
      ],
      faq: [
        {
          q: 'What happens with a very long item?',
          a: 'It is not cut off or shrunk. You are told which item does not fit on one line, so you can shorten it or reduce the margins.',
        },
        {
          q: 'Can I use emoji or Chinese characters?',
          a: 'Latin letters, Korean, digits and common symbols work. Emoji and Chinese characters are not in the embedded font, so the page lists the characters it cannot print and disables the download.',
        },
      ],
    },
  },

  printGuideHeading: 'Print at actual size (100%)',
  printGuide: [
    'Open the downloaded PDF and open the print dialog.',
    'Under size or scale, choose “Actual size” or 100%. Turn off “Fit to page” and “Shrink oversized pages”.',
    'Set the paper to the same size as the PDF, A4 or Letter.',
    'Check the first sheet with a ruler. Printers differ in how close to the edge they can print.',
  ],
  editorHeading: 'Settings and preview',
  noScript:
    'This form is built as a PDF by JavaScript inside your browser. The instructions and answers below are readable as they are.',
  otherFormsHeading: 'Other forms',
  backToHub: 'All printable forms',
};
